import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import VerdictBadge from '@/components/base/VerdictBadge';
import ExposeCard from '@/components/base/ExposeCard';
import { useSeo } from '@/hooks/useSeo';
import { formatHeat, getExposeById, getRelatedExposes } from '@/lib/content';
import { credibilityBarClass, credibilityLabel, verdictMeta } from '@/lib/verdict';

const statusMeta: Record<string, { label: string; cls: string; icon: string }> = {
  solid: { label: '已印证', cls: 'text-secondary-700 bg-secondary-100', icon: 'ri-check-double-line' },
  weak: { label: '待加强', cls: 'text-accent-800 bg-accent-100', icon: 'ri-error-warning-line' },
  fake: { label: '已证伪', cls: 'text-primary-700 bg-primary-100', icon: 'ri-close-circle-line' },
};

export default function ExposeDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getExposeById(id) : undefined;

  useSeo({
    title: item ? `${item.title} · 核实结果 | 看料爆料` : '核实详情 | 看料爆料',
    description: item
      ? `${item.summary.slice(0, 90)}——看料爆料对该爆料的判定为「${verdictMeta[item.verdict].label}」，可信度评分 ${item.credibility}，附证据链与核实时间线。`
      : '看料爆料爆料核实详情页',
    keywords: item
      ? `${item.tags.join(',')},看料爆料,看料爆料真假,看料爆料核实`
      : '看料爆料,爆料核实',
    type: 'article',
    image: item?.cover,
    jsonLd: item
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: item.title,
            description: item.summary,
            image: item.cover,
            datePublished: item.publishedAt,
            dateModified: item.updatedAt,
            articleSection: item.category,
            keywords: item.tags.join(','),
            author: { '@type': 'Organization', name: '看料爆料' },
            publisher: { '@type': 'Organization', name: '看料爆料' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
              { '@type': 'ListItem', position: 2, name: '热料榜单', item: `${typeof window !== 'undefined' ? window.location.origin : ''}/ranking` },
              { '@type': 'ListItem', position: 3, name: item.title },
            ],
          },
        ]
      : undefined,
  });

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1">
          <div className="container-page py-24 text-center">
            <span className="mx-auto flex w-16 h-16 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
              <i className="ri-file-search-line"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条核实记录不存在</h1>
            <p className="mt-3 text-sm text-foreground-600">
              可能链接有误，或者该内容已经调整。可以回到榜单看看其他核实记录。
            </p>
            <Link
              to="/ranking"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回热料榜单
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedExposes(item, 3);
  const meta = verdictMeta[item.verdict];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="container-page py-6 md:py-8">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: '热料榜单', to: '/ranking' },
                { label: item.title },
              ]}
            />
          </div>
        </div>

        <div className="container-page py-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <article className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-md border border-background-300 px-2.5 py-1 text-xs text-foreground-600">
                    {item.category}
                  </span>
                  <VerdictBadge verdict={item.verdict} size="md" />
                  {item.hot && (
                    <span className="inline-flex items-center gap-1 text-xs text-primary-600">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-fire-line"></i>
                      </span>
                      高热爆料
                    </span>
                  )}
                </div>

                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl md:leading-[1.3]">
                  {item.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </span>
                    首发 {item.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-refresh-line"></i>
                    </span>
                    {item.updatedAt} 更新
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-fire-line"></i>
                    </span>
                    热度 {formatHeat(item.heat)}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    阅读约 {item.readTime}
                  </span>
                </div>

                <div className="mt-6 w-full aspect-[16/9] overflow-hidden rounded-xl border border-background-200 bg-background-100">
                  <img
                    src={item.cover}
                    alt={`${item.title} 核实配图`}
                    title={`${item.title} - 看料爆料核实`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </header>

              <p className="mt-8 border-l-2 border-primary-400 pl-4 text-base leading-relaxed text-foreground-700">
                {item.summary}
              </p>

              <section className="mt-8 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex w-9 h-9 items-center justify-center rounded-lg text-lg ${meta.badge}`}>
                    <i className={meta.icon}></i>
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">核实判定：{meta.label}</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{meta.desc}</p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-800">{item.conclusion}</p>
              </section>

              <section className="mt-10" aria-labelledby="timeline-title">
                <h2 id="timeline-title" className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-history-line"></i>
                  </span>
                  核实时间线
                </h2>
                <ol className="mt-6 relative border-l border-background-300 pl-6 space-y-7">
                  {item.timeline.map((step) => (
                    <li key={step.time} className="relative">
                      <span className="absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full border-2 border-background-50 bg-primary-500"></span>
                      <p className="text-xs font-medium tracking-wide text-primary-600">{step.time}</p>
                      <h3 className="mt-1 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{step.detail}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="mt-10" aria-labelledby="evidence-title">
                <h2 id="evidence-title" className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-file-search-line"></i>
                  </span>
                  证据链梳理
                </h2>
                <div className="mt-5 space-y-3">
                  {item.evidence.map((ev) => {
                    const sm = statusMeta[ev.status];
                    return (
                      <div
                        key={ev.title}
                        className="flex flex-col gap-2 rounded-xl border border-background-200 bg-background-50 p-4 md:flex-row md:items-start md:gap-4"
                      >
                        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${sm.cls}`}>
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className={sm.icon}></i>
                          </span>
                          {sm.label}
                        </span>
                        <div>
                          <h3 className="font-heading text-base font-semibold text-foreground-950">{ev.title}</h3>
                          <p className="mt-1 text-sm leading-relaxed text-foreground-600">{ev.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="mt-10" aria-labelledby="analysis-title">
                <h2 id="analysis-title" className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-article-line"></i>
                  </span>
                  深度分析
                </h2>
                <div className="mt-5 space-y-4">
                  {item.analysis.map((para) => (
                    <p key={para} className="text-sm leading-relaxed text-foreground-700">
                      {para}
                    </p>
                  ))}
                </div>
              </section>

              <div className="mt-8 flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/ranking?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-100 px-3.5 py-1.5 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-5">
                <div className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">可信度评分</h2>
                  <div className="mt-4 flex items-end gap-2">
                    <span className="font-heading text-4xl font-bold text-foreground-950">{item.credibility}</span>
                    <span className="mb-1 text-sm text-foreground-500">/ 100 · {credibilityLabel(item.credibility)}</span>
                  </div>
                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className={`h-full rounded-full ${credibilityBarClass(item.credibility)}`}
                      style={{ width: `${item.credibility}%` }}
                    ></div>
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                    评分反映当前证据的充分程度，会随新证据出现而调整。
                  </p>

                  <dl className="mt-5 space-y-3 border-t border-background-200 pt-5 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">信息类型</dt>
                      <dd className="text-right text-foreground-800">{item.category}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">来源渠道</dt>
                      <dd className="text-right text-foreground-800">{item.source}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">核实节点</dt>
                      <dd className="text-right text-foreground-800">{item.timeline.length} 个</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">最新更新</dt>
                      <dd className="text-right text-foreground-800">{item.updatedAt}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-xl border border-accent-200 bg-accent-50 p-5 md:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">对这条判定有疑问？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    如果你掌握更可靠的信息，欢迎提交给我们，核实后会更正判定并注明来源。
                  </p>
                  <Link
                    to="/about#submit"
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent-600 px-4 py-2.5 text-sm font-medium text-background-50 hover:bg-accent-700 transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    提交线索或纠错
                  </Link>
                </div>

                <div className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground-950">相关核实记录</h2>
                  <ul className="mt-3 space-y-3">
                    {related.map((rel) => (
                      <li key={rel.id} className="border-t border-background-200 pt-3 first:border-t-0 first:pt-0">
                        <Link to={`/expose/${rel.id}`} className="group block">
                          <div className="flex items-center gap-2">
                            <VerdictBadge verdict={rel.verdict} showIcon={false} />
                            <span className="text-xs text-foreground-500">{rel.category}</span>
                          </div>
                          <p className="mt-1.5 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {rel.title}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14 border-t border-background-200 pt-10" aria-labelledby="more-title">
            <h2 id="more-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              继续看看其他热料
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((rel) => (
                <ExposeCard key={rel.id} item={rel} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}